/**
 * Class Allocation Page
 * Split-panel layout: unassigned students (left) + class group grid (right).
 * Drag-and-drop interface for assigning students to class groups.
 * Includes auto-assignment algorithm and print functionality.
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useClassAllocation } from '../../hooks/useClassAllocation';
import { useToast } from '../../hooks/use-toast';
import { useCourseTime } from '../../contexts/CourseTimeContext';
import { CourseTimeToggle } from '../ui/course-time-toggle';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Loader2, AlertCircle, Trash2, X, ChevronLeft,
  Award, Wand2, Download, GraduationCap, Pencil, Search, Printer,
} from 'lucide-react';
import { ClassPrintView } from '../students/ClassPrintView';

// ─── Occupancy bar ────────────────────────────────────────────────────────────

function OccupancyBar({ current, max }) {
  const pct = max > 0 ? (current / max) * 100 : 0;
  const isOver = current > max;
  const isFull = !isOver && pct >= 100;
  const isInProgress = !isOver && !isFull && pct > 0;
  return (
    <div className="h-1.5 rounded-full bg-muted flex-1">
      <div
        className={`h-1.5 rounded-full transition-all ${isOver ? 'bg-red-500' : isFull ? 'bg-green-500' : ''}`}
        style={{
          width: `${Math.min(pct, 100)}%`,
          ...(isInProgress ? { background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' } : {}),
        }}
      />
    </div>
  );
}

// ─── Year badge colour helper ─────────────────────────────────────────────────

function yearBadgeClass(yearOrder) {
  switch (yearOrder) {
    case 1: return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300';
    case 2: return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
    case 3: return 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300';
    case 4: return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300';
    case 5: return 'bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-300';
    case 6: return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
    default: return 'bg-muted text-muted-foreground';
  }
}

// ─── Student card ─────────────────────────────────────────────────────────────

function StudentCard({ student, isDragging = false, highlighted = false }) {
  const hasGroup = student.friend_group && student.friend_group.length > 0;
  const groupSize = hasGroup ? student.friend_group.length + 1 : 0;

  const cardColor = student.is_cinnire
    ? 'bg-amber-50 border-amber-300 dark:bg-amber-900/30 dark:border-amber-700'
    : 'bg-card border-border';

  return (
    <div
      className={`relative rounded-md border p-2 text-sm select-none transition-shadow ${cardColor} ${
        isDragging ? 'opacity-50' : 'hover:shadow-sm'
      } ${highlighted ? 'ring-2 ring-amber-400' : ''}`}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">
            {student.last_name}, {student.first_name}
          </div>
          <div className="text-xs text-muted-foreground">{student.student_id}</div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {student.is_cinnire && (
            <Award className="h-3.5 w-3.5 text-amber-500" />
          )}
          <span
            className={`inline-block w-2 h-2 rounded-full mt-0.5 ${
              student.gender === 'M' ? 'bg-blue-400' : student.gender === 'F' ? 'bg-pink-400' : 'bg-gray-400'
            }`}
            title={student.gender === 'M' ? 'Male' : student.gender === 'F' ? 'Female' : 'Other'}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mt-1">
        {student.school_year && (
          <span className={`text-xs px-1.5 py-0.5 rounded ${yearBadgeClass(student.school_year_order)}`}>
            {student.school_year}
          </span>
        )}
        {student.house_name && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 truncate max-w-[80px]">
            {student.house_name}
          </span>
        )}
        {hasGroup && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
            +{groupSize - 1}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Draggable student ────────────────────────────────────────────────────────

function DraggableStudent({ student, readOnly = false, highlighted = false }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: student.uuid,
    disabled: readOnly,
    data: { student },
  });

  return (
    <div ref={setNodeRef} {...listeners} {...attributes} className={readOnly ? '' : 'cursor-grab active:cursor-grabbing'}>
      <StudentCard student={student} isDragging={isDragging} highlighted={highlighted} />
    </div>
  );
}

// ─── Unassigned panel ─────────────────────────────────────────────────────────

function UnassignedPanel({ students, search, yearFilter, onYearFilterChange, cinnireFilter, onCinnireFilterChange, yearOptions }) {
  const { t } = useTranslation();
  const { isOver, setNodeRef } = useDroppable({ id: 'unassigned' });

  const filtered = useMemo(() => {
    let list = students.filter((s) => !s.class_group_uuid);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (s) =>
          s.first_name.toLowerCase().includes(q) ||
          s.last_name.toLowerCase().includes(q) ||
          s.student_id.toLowerCase().includes(q)
      );
    }
    if (yearFilter && yearFilter !== 'all') {
      list = list.filter((s) => s.school_year === yearFilter);
    }
    if (cinnireFilter) {
      list = list.filter((s) => s.is_cinnire);
    }
    return list;
  }, [students, search, yearFilter, cinnireFilter]);

  return (
    <div className="flex flex-col h-full border-r bg-muted/30">
      <div className="p-3 border-b space-y-2">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-sm">{t('classAllocation.unassigned')}</span>
          <Badge variant="secondary">{filtered.length}</Badge>
        </div>

        <div className="flex gap-1 flex-wrap">
          <Select value={yearFilter} onValueChange={onYearFilterChange}>
            <SelectTrigger className="h-7 text-xs flex-1 min-w-[90px]">
              <SelectValue placeholder={t('classAllocation.filterAllYears')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('classAllocation.filterAllYears')}</SelectItem>
              {yearOptions.map((y) => (
                <SelectItem key={y.year} value={y.year}>{y.year}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <button
            onClick={() => onCinnireFilterChange(!cinnireFilter)}
            className={`h-7 px-2 text-xs rounded-md border transition-colors ${
              cinnireFilter
                ? 'bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-900/40 dark:border-amber-700 dark:text-amber-300'
                : 'bg-background border-border text-muted-foreground hover:bg-muted'
            }`}
          >
            <Award className="inline h-3 w-3 mr-1" />
            {t('classAllocation.cinnire')}
          </button>
        </div>
      </div>

      <div
        ref={setNodeRef}
        className={`flex-1 overflow-y-auto p-2 space-y-1.5 transition-colors ${
          isOver ? 'bg-primary/5' : ''
        }`}
      >
        {filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-8">
            {t('classAllocation.noStudents')}
          </p>
        ) : (
          filtered.map((s) => <DraggableStudent key={s.uuid} student={s} />)
        )}
      </div>
    </div>
  );
}

// ─── Class card (droppable) ───────────────────────────────────────────────────

function ClassCard({ classGroup, students, onClick, isSelected, matchCount = 0, searchActive = false }) {
  const { isOver, setNodeRef } = useDroppable({ id: classGroup.uuid });
  const { current_enrollment, capacity, is_cinnire_class } = classGroup;
  const hasMatch = matchCount > 0;

  const baseStyle = hasMatch
    ? 'border-amber-400'
    : is_cinnire_class
    ? 'border-amber-300 dark:border-amber-700'
    : 'border-border';
  const bgStyle = isSelected
    ? 'bg-primary/5 border-primary'
    : hasMatch
    ? 'bg-amber-50/30 dark:bg-amber-900/10'
    : is_cinnire_class
    ? 'bg-amber-50/50 dark:bg-amber-900/10'
    : 'bg-card';

  return (
    <div
      ref={setNodeRef}
      onClick={onClick}
      className={`rounded-lg border p-3 cursor-pointer transition-all ${baseStyle} ${bgStyle} ${
        isOver ? 'ring-2 ring-primary ring-offset-1' : 'hover:shadow-sm'
      } ${hasMatch ? 'ring-1 ring-amber-400' : ''} ${searchActive && !hasMatch ? 'opacity-40' : ''}`}
    >
      <div className="flex items-start justify-between gap-1 mb-2">
        <div className="font-semibold text-sm truncate flex items-center gap-1">
          {is_cinnire_class && <Award className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
          {classGroup.name}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {hasMatch && (
            <span className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 text-xs font-semibold px-1.5 py-0.5 rounded-full">
              {matchCount}
            </span>
          )}
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {current_enrollment}/{capacity}
          </span>
        </div>
      </div>

      <OccupancyBar current={current_enrollment} max={capacity} />

      <div className="flex flex-wrap gap-1 mt-2">
        {classGroup.school_year_breakdown?.map((yb) => (
          <span
            key={yb.year}
            className={`text-xs px-1 py-0.5 rounded ${yearBadgeClass(yb.order)}`}
          >
            {yb.year}: {yb.count}
          </span>
        ))}
      </div>

      {!is_cinnire_class && (
        <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-0.5">
            <span className="inline-block w-2 h-2 rounded-full bg-pink-400" />
            {classGroup.gender_breakdown?.female ?? 0}F
          </span>
          <span className="flex items-center gap-0.5">
            <span className="inline-block w-2 h-2 rounded-full bg-blue-400" />
            {classGroup.gender_breakdown?.male ?? 0}M
          </span>
        </div>
      )}

      {classGroup.teacher_name && (
        <div className="mt-1 text-xs text-muted-foreground truncate">{classGroup.teacher_name}</div>
      )}
    </div>
  );
}

// ─── Class detail panel ───────────────────────────────────────────────────────

function ClassDetailPanel({ classGroup, students, courseUuid, onClose, searchQuery = '' }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(classGroup.name);

  // Keep local name in sync if classGroup prop changes (e.g. after refetch)
  React.useEffect(() => { setNameValue(classGroup.name); }, [classGroup.name]);

  const assignedStudents = useMemo(
    () => students.filter((s) => classGroup.students.includes(s.uuid))
      .sort((a, b) => a.last_name.localeCompare(b.last_name)),
    [students, classGroup.students]
  );

  const matchUuids = useMemo(() => {
    if (!searchQuery) return new Set();
    const q = searchQuery.toLowerCase();
    return new Set(
      assignedStudents
        .filter((s) => {
          const name = `${s.first_name} ${s.last_name}`.toLowerCase();
          return name.includes(q) || (s.student_id || '').includes(q);
        })
        .map((s) => s.uuid)
    );
  }, [assignedStudents, searchQuery]);

  const teachersQuery = useQuery({
    queryKey: ['class-teachers'],
    queryFn: api.classAllocation.getTeachers,
    staleTime: 5 * 60 * 1000,
  });

  const teacherMutation = useMutation({
    mutationFn: (teacherId) => api.classAllocation.assignTeacher(classGroup.uuid, teacherId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['class-allocation', courseUuid] });
      queryClient.invalidateQueries({ queryKey: ['class-allocation', 'summary'] });
    },
  });

  const renameMutation = useMutation({
    mutationFn: (name) => api.classAllocation.renameClass(classGroup.uuid, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['class-allocation', courseUuid] });
    },
    onError: () => {
      setNameValue(classGroup.name);
    },
  });

  const teachers = teachersQuery.data ?? [];
  const currentTeacherId = teachers.find((t) => t.teacher_name === classGroup.teacher_name)?.teacher_id ?? '';

  function handleTeacherChange(e) {
    const val = e.target.value;
    teacherMutation.mutate(val === '' ? null : Number(val));
  }

  function commitRename() {
    setEditingName(false);
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === classGroup.name) {
      setNameValue(classGroup.name);
      return;
    }
    renameMutation.mutate(trimmed);
  }

  function handleNameKeyDown(e) {
    if (e.key === 'Enter') commitRename();
    if (e.key === 'Escape') { setEditingName(false); setNameValue(classGroup.name); }
  }

  return (
    <div className="flex flex-col h-full border-l bg-card">
      <div className="p-3 border-b flex items-center justify-between">
        <div className="flex-1 min-w-0 mr-2">
          {editingName ? (
            <input
              autoFocus
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onBlur={commitRename}
              onKeyDown={handleNameKeyDown}
              className="w-full text-sm font-semibold bg-background border border-primary rounded px-1.5 py-0.5 outline-none"
              maxLength={50}
            />
          ) : (
            <button
              onClick={() => setEditingName(true)}
              className="font-semibold text-sm flex items-center gap-1 hover:text-primary transition-colors group text-left w-full"
              title={t('classAllocation.renameClass', 'Click to rename')}
            >
              {classGroup.is_cinnire_class && <Award className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
              <span className="truncate">{nameValue}</span>
              <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-40 shrink-0" />
            </button>
          )}
          <div className="text-xs text-muted-foreground">
            {classGroup.current_enrollment}/{classGroup.capacity} {t('classAllocation.students')}
          </div>
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-muted shrink-0">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Teacher assignment */}
      <div className="px-3 py-2 border-b">
        <label className="text-xs font-medium text-muted-foreground block mb-1">
          {t('classAllocation.teacher')}
        </label>
        <select
          className="w-full text-sm rounded-md border bg-background px-2 py-1.5 disabled:opacity-50"
          value={currentTeacherId}
          onChange={handleTeacherChange}
          disabled={teacherMutation.isPending || teachersQuery.isLoading}
        >
          <option value="">{t('classAllocation.noTeacher')}</option>
          {teachers.map((teacher) => (
            <option key={teacher.teacher_id} value={teacher.teacher_id}>
              {teacher.teacher_name}
            </option>
          ))}
        </select>
        {teacherMutation.isError && (
          <p className="text-xs text-destructive mt-1">{t('classAllocation.teacherAssignError')}</p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {assignedStudents.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-8">
            {t('classAllocation.noAssigned')}
          </p>
        ) : (
          assignedStudents.map((s) => <DraggableStudent key={s.uuid} student={s} highlighted={matchUuids.has(s.uuid)} />)
        )}
      </div>
    </div>
  );
}

// ─── Auto-assign dialog ───────────────────────────────────────────────────────

function AutoAssignDialog({ onConfirm, onCancel, isLoading }) {
  const { t } = useTranslation();
  const [reassignAll, setReassignAll] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-card border rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
        <div className="flex items-center gap-2 mb-3">
          <Wand2 className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">{t('classAllocation.autoAssign')}</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          {t('classAllocation.autoAssignConfirm')}
        </p>
        <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={reassignAll}
            onChange={(e) => setReassignAll(e.target.checked)}
            className="rounded"
          />
          {t('classAllocation.autoAssignReassignAll')}
        </label>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={() => onConfirm(reassignAll)} disabled={isLoading}>
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t('classAllocation.autoAssign')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Clear dialog ─────────────────────────────────────────────────────────────

function ClearDialog({ onConfirm, onCancel, isLoading, error }) {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(false);
  const [password, setPassword] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-card border rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
        <div className="flex items-center gap-2 mb-3 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <h3 className="font-semibold">{t('classAllocation.clearAll')}</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{t('classAllocation.clearConfirm')}</p>

        <label className="flex items-start gap-2 text-sm mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="rounded mt-0.5"
          />
          {t('classAllocation.clearAcknowledge')}
        </label>

        <Input
          type="password"
          placeholder={t('classAllocation.clearPasswordPlaceholder')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mb-3"
        />

        {error && (
          <p className="text-xs text-destructive mb-3">{t('classAllocation.clearPasswordError')}</p>
        )}

        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm(password)}
            disabled={!acknowledged || !password || isLoading}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t('classAllocation.clearAll')}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ─── Course selection cards ───────────────────────────────────────────────────

function CourseSelectionGrid({ courses, onSelect, isLoading, error, t }) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>{t('common.loading', 'Loading...')}</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center gap-2 text-destructive">
        <AlertCircle className="h-4 w-4" />
        <span>{t('common.loadError', 'Failed to load courses.')}</span>
      </div>
    );
  }

  const n = courses.length;
  const cols = n <= 2 ? 2 : n === 3 ? 3 : n === 4 ? 2 : n <= 6 ? 3 : 4;
  const gridClass =
    cols === 2 ? 'grid-cols-2 max-w-3xl' :
    cols === 3 ? 'grid-cols-3 max-w-5xl' :
                 'grid-cols-4 max-w-7xl';

  return (
    <div className={`grid gap-5 mx-auto w-full ${gridClass}`}>
      {courses.map((course) => {
        const pct = course.total > 0 ? (course.assigned / course.total) * 100 : 0;
        const isComplete = course.total > 0 && course.unassigned === 0;

        return (
          <button
            key={course.uuid}
            onClick={() => onSelect(course.uuid)}
            className="text-left rounded-xl border bg-card p-6 hover:border-primary/50 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between gap-2 mb-4">
              <div className="font-semibold text-lg leading-tight">{course.name}</div>
              {isComplete && (
                <span className="text-xs bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/40 dark:text-green-400 dark:border-green-700 rounded-full px-2 py-0.5 flex-shrink-0">
                  {t('classAllocation.complete')}
                </span>
              )}
            </div>

            {/* Assignment progress */}
            <div className="space-y-1.5 mb-4">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{t('classAllocation.studentsAssigned')}</span>
                <span className="font-medium text-foreground">{course.assigned} / {course.total}</span>
              </div>
              <div className="h-2.5 rounded-full bg-muted">
                <div
                  className={`h-2.5 rounded-full transition-all ${isComplete ? 'bg-green-500' : ''}`}
                  style={{ width: `${Math.min(pct, 100)}%`, ...(isComplete ? {} : { background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' }) }}
                />
              </div>
            </div>

            {/* Class + cinnirí counts */}
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <GraduationCap className="h-3.5 w-3.5" />
                {course.class_count} {t('classAllocation.classes')}
                {course.cinnire_class_count > 0 && (
                  <span className="text-amber-600">&amp; {course.cinnire_class_count} Cinnirí</span>
                )}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ClassAllocation() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { showAllTime } = useCourseTime();

  const [selectedCourse, setSelectedCourse] = useState('');
  const [search, setSearch] = useState('');
  const [yearFilter, setYearFilter] = useState('all');
  const [cinnireFilter, setCinnireFilter] = useState(false);
  const [selectedClassUuid, setSelectedClassUuid] = useState(null);
  const [activeDragStudent, setActiveDragStudent] = useState(null);
  const [autoAssignDialogOpen, setAutoAssignDialogOpen] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearError, setClearError] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  // Course summary list
  const summaryQuery = useQuery({
    queryKey: ['class-allocation', 'summary', showAllTime ? 'all' : 'upcoming'],
    queryFn: () => api.classAllocation.getSummary(showAllTime),
    staleTime: 30 * 1000,
  });

  const {
    course, classes, students,
    isLoading, error,
    moveStudents,
    autoAssign, isAutoAssigning,
    clearAssignments, isClearing,
    refetchBoard,
  } = useClassAllocation(selectedCourse);

  // Derive year options from students
  const yearOptions = useMemo(() => {
    const seen = new Map();
    (students || []).forEach((s) => {
      if (s.school_year && !seen.has(s.school_year)) {
        seen.set(s.school_year, s.school_year_order);
      }
    });
    return Array.from(seen.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([year]) => ({ year }));
  }, [students]);

  const studentLookup = useMemo(() => {
    const map = {};
    (students || []).forEach((s) => { map[s.uuid] = s; });
    return map;
  }, [students]);

  const selectedClass = useMemo(
    () => classes.find((cg) => cg.uuid === selectedClassUuid) ?? null,
    [classes, selectedClassUuid]
  );

  const classMatchCounts = useMemo(() => {
    if (!search) return {};
    const q = search.toLowerCase();
    const counts = {};
    for (const cg of classes) {
      counts[cg.uuid] = (students || []).filter((s) => {
        if (!cg.students.includes(s.uuid)) return false;
        const name = `${s.first_name} ${s.last_name}`.toLowerCase();
        return name.includes(q) || (s.student_id || '').includes(q);
      }).length;
    }
    return counts;
  }, [classes, students, search]);

  // ── DnD handlers ───────────────────────────────────────────────────────────

  function handleDragStart(event) {
    const student = studentLookup[event.active.id];
    setActiveDragStudent(student ?? null);
  }

  async function handleDragEnd(event) {
    setActiveDragStudent(null);
    const { active, over } = event;
    if (!over) return;

    const student = studentLookup[active.id];
    if (!student) return;

    const targetId = over.id; // class uuid or 'unassigned'
    const currentClassUuid = student.class_group_uuid;
    if (targetId === currentClassUuid) return;

    const targetClass = targetId === 'unassigned' ? null : classes.find((cg) => cg.uuid === targetId);

    // Cinnirí validation
    if (targetClass) {
      if (targetClass.is_cinnire_class && !student.is_cinnire) {
        toast({
          title: t('classAllocation.error.cinnireOnly'),
          description: t('classAllocation.error.cinnireOnlyDesc'),
          variant: 'destructive',
        });
        return;
      }
      if (!targetClass.is_cinnire_class && student.is_cinnire) {
        toast({
          title: t('classAllocation.error.cinnireMustGoToClass'),
          description: t('classAllocation.error.cinnireMustGoToClassDesc'),
          variant: 'destructive',
        });
        return;
      }
    }

    // Collect friend group
    const friendUuids = student.friend_group ?? [];
    const friendsToMove = friendUuids
      .map((u) => studentLookup[u])
      .filter(Boolean)
      .filter((s) => s.class_group_uuid !== targetId);

    const allUuids = [student.uuid, ...friendsToMove.map((s) => s.uuid)];

    try {
      await moveStudents(allUuids, targetClass?.uuid ?? null);
      if (friendsToMove.length > 0) {
        toast({
          title: t('classAllocation.friendGroupMoved', { count: allUuids.length }),
          description: t('classAllocation.friendGroupMovedDesc'),
        });
      }
    } catch (err) {
      const reason = err?.response?.data?.details?.[0]?.reason;
      if (reason === 'capacity') {
        toast({
          title: t('classAllocation.error.capacityExceeded'),
          description: t('classAllocation.error.capacityExceededDesc'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('common.error', 'Error'),
          description: err?.response?.data?.error ?? t('common.unexpectedError', 'Something went wrong.'),
          variant: 'destructive',
        });
      }
    }
  }

  // ── Auto-assign handler ────────────────────────────────────────────────────

  async function handleAutoAssign(reassignAll) {
    setAutoAssignDialogOpen(false);
    try {
      const result = await autoAssign(selectedCourse, reassignAll);
      toast({
        title: t('classAllocation.autoAssignSuccess', { count: result.assigned_count }),
        description: result.warnings?.length
          ? result.warnings.slice(0, 3).join('\n')
          : undefined,
      });
    } catch {
      toast({
        title: t('classAllocation.autoAssignError'),
        variant: 'destructive',
      });
    }
  }

  // ── Clear handler ─────────────────────────────────────────────────────────

  async function handleClear(password) {
    setClearError(false);
    try {
      const result = await clearAssignments(selectedCourse, password);
      await refetchBoard();
      setClearDialogOpen(false);
      toast({
        title: t('classAllocation.clearedAll'),
        description: t('classAllocation.clearedAllCount', { count: result.cleared_count }),
      });
    } catch (err) {
      if (err?.response?.status === 403) {
        setClearError(true);
      } else {
        setClearDialogOpen(false);
        toast({ title: t('classAllocation.clearError'), variant: 'destructive' });
      }
    }
  }

  // ── CSV export ─────────────────────────────────────────────────────────────

  function handleExportCSV() {
    try {
      const assignedClasses = classes.filter((cg) => cg.current_enrollment > 0);
      const rows = [['Class', 'Teacher', 'Student ID', 'Last Name', 'First Name', 'School Year', 'Gender', 'House']];

      assignedClasses.forEach((cg) => {
        students
          .filter((s) => cg.students.includes(s.uuid))
          .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''))
          .forEach((s) => {
            rows.push([
              cg.name,
              cg.teacher_name || '',
              s.student_id || '',
              s.last_name || '',
              s.first_name || '',
              s.school_year || '',
              s.gender === 'M' ? 'B' : s.gender === 'F' ? 'C' : '',
              s.house_name || '',
            ]);
          });
        rows.push([]);
      });

      const csv = rows.map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = (course?.name ?? 'class-lists').replace(/[^a-zA-Z0-9À-ÿ _-]/g, '_');
      a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast({ title: `Exported ${assignedClasses.length} class(es)`, variant: 'success' });
    } catch (err) {
      console.error('CSV export error:', err);
      toast({ title: 'Export failed', description: err.message, variant: 'destructive' });
    }
  }

  // ── Course selection screen ────────────────────────────────────────────────

  if (!selectedCourse) {
    return (
      <div className="pb-8">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight border-l-4 pl-3" style={{ borderColor: '#F28482' }}>
              {t('classAllocation.title')}
            </h1>
            <p className="text-muted-foreground mt-1 pl-3">{t('classAllocation.selectCoursePrompt')}</p>
          </div>
          <CourseTimeToggle />
        </div>
        <CourseSelectionGrid
          courses={summaryQuery.data ?? []}
          onSelect={setSelectedCourse}
          isLoading={summaryQuery.isLoading}
          error={summaryQuery.error}
          t={t}
        />
      </div>
    );
  }

  // ── Main board ─────────────────────────────────────────────────────────────

  const regularClasses = classes.filter((cg) => !cg.is_cinnire_class);
  const cinnireClass = classes.find((cg) => cg.is_cinnire_class);
  const totalStudents = students.length;
  const assignedCount = students.filter((s) => s.class_group_uuid).length;

  return (
    <>
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex flex-col h-screen">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b bg-background shrink-0">
            <button
              onClick={() => setSelectedCourse('')}
              className="p-1 rounded hover:bg-muted"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="flex-1 min-w-0">
              <h1 className="font-semibold truncate">{course?.name ?? '...'}</h1>
              <p className="text-xs text-muted-foreground">
                {t('classAllocation.assigned', { assigned: assignedCount, total: totalStudents })}
              </p>
            </div>

            <div className="relative w-48">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                placeholder={t('classAllocation.searchStudents')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 pl-8 text-xs"
              />
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                disabled={!course}
              >
                <Download className="h-4 w-4 mr-1.5" />
                {t('classAllocation.exportCsv')}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setPrintOpen(true)}
                disabled={!course || isLoading}
              >
                <Printer className="h-4 w-4 mr-1.5" />
                {t('classAllocation.printClasses', 'Print Classes')}
              </Button>

              <Button
                size="sm"
                onClick={() => setAutoAssignDialogOpen(true)}
                disabled={isAutoAssigning || isLoading}
              >
                {isAutoAssigning
                  ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  : <Wand2 className="h-4 w-4 mr-1.5" />
                }
                {t('classAllocation.autoAssign')}
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setClearDialogOpen(true)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Board */}
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center text-destructive gap-2">
              <AlertCircle className="h-5 w-5" />
              <span>{t('common.loadError', 'Failed to load class data.')}</span>
            </div>
          ) : (
            <div className="flex-1 flex min-h-0">
              {/* Left: unassigned panel */}
              <div className="w-56 shrink-0 flex flex-col min-h-0">
                <UnassignedPanel
                  students={students}
                  search={search}
                  yearFilter={yearFilter}
                  onYearFilterChange={setYearFilter}
                  cinnireFilter={cinnireFilter}
                  onCinnireFilterChange={setCinnireFilter}
                  yearOptions={yearOptions}
                />
              </div>

              {/* Centre: class grid */}
              <div className="flex-1 overflow-y-auto p-4 min-h-0">
                {/* Regular classes grid */}
                <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 mb-4">
                  {regularClasses.map((cg) => (
                    <ClassCard
                      key={cg.uuid}
                      classGroup={cg}
                      students={students}
                      onClick={() => setSelectedClassUuid(cg.uuid === selectedClassUuid ? null : cg.uuid)}
                      isSelected={cg.uuid === selectedClassUuid}
                      matchCount={classMatchCounts[cg.uuid] ?? 0}
                      searchActive={!!search}
                    />
                  ))}
                </div>

                {/* Cinnirí class (separate, full-width strip) */}
                {cinnireClass && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                      {t('classAllocation.cinnireClass')}
                    </p>
                    <div className="max-w-xs">
                      <ClassCard
                        classGroup={cinnireClass}
                        students={students}
                        onClick={() => setSelectedClassUuid(cinnireClass.uuid === selectedClassUuid ? null : cinnireClass.uuid)}
                        isSelected={cinnireClass.uuid === selectedClassUuid}
                        matchCount={classMatchCounts[cinnireClass.uuid] ?? 0}
                        searchActive={!!search}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Right: class detail panel */}
              {selectedClass && (
                <div className="w-64 shrink-0 flex flex-col min-h-0">
                  <ClassDetailPanel
                    classGroup={selectedClass}
                    students={students}
                    courseUuid={selectedCourse}
                    onClose={() => setSelectedClassUuid(null)}
                    searchQuery={search}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <DragOverlay>
          {activeDragStudent && (
            <div className="opacity-90 rotate-1 shadow-lg w-52">
              <StudentCard student={activeDragStudent} isDragging={false} />
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Auto-assign dialog */}
      {autoAssignDialogOpen && (
        <AutoAssignDialog
          onConfirm={handleAutoAssign}
          onCancel={() => setAutoAssignDialogOpen(false)}
          isLoading={isAutoAssigning}
        />
      )}

      {/* Clear dialog */}
      {clearDialogOpen && (
        <ClearDialog
          onConfirm={handleClear}
          onCancel={() => { setClearDialogOpen(false); setClearError(false); }}
          isLoading={isClearing}
          error={clearError}
        />
      )}

      {/* Print overlay */}
      {printOpen && (
        <ClassPrintView
          course={course}
          classes={classes}
          students={students}
          onClose={() => setPrintOpen(false)}
        />
      )}
    </>
  );
}
